/* ============================================================================
 *  SEÇÃO DE INSCRIÇÃO DO PROCESSO SELETIVO — TEMPORÁRIA
 * ============================================================================
 *  Aparece no topo da home enquanto `psConfig.ativo` for true.
 *  Todo o texto vem de `psConfig.homeCta`, em src/data/processoSeletivoData.js.
 *  Para tirar do ar: vire `psConfig.ativo` para false.
 *  Para apagar de vez: veja COMO-REMOVER-O-PS.md na raiz do projeto.
 * ========================================================================== */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowDown, CalendarClock, Sprout, Code2 } from 'lucide-react';
import { psConfig } from '../../data/processoSeletivoData';

/* Dias de calendário até o fim das inscrições.
 * Retorna null se o prazo já passou ou se `deadlineDate` não estiver configurada. */
const calcularDiasRestantes = () => {
  if (!psConfig.deadlineDate) return null;
  const fim = new Date(psConfig.deadlineDate);
  if (Number.isNaN(fim.getTime())) return null;

  const agora = new Date();
  if (agora.getTime() > fim.getTime()) return null;

  // Compara datas "cheias", ignorando a hora, para não contar um dia a mais
  const inicioDoDia = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((inicioDoDia(fim) - inicioDoDia(agora)) / (1000 * 60 * 60 * 24));
};

const ProcessoSeletivoCTA = () => {
  const [diasRestantes, setDiasRestantes] = useState(calcularDiasRestantes);

  // Recalcula de hora em hora, para a contagem virar sozinha à meia-noite
  useEffect(() => {
    const id = setInterval(() => setDiasRestantes(calcularDiasRestantes()), 60 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const { homeCta, cronograma } = psConfig;

  /* A entrada é feita em CSS (.ps-rise): se o JS falhar, o conteúdo aparece
   * do mesmo jeito — o que não aconteceria com animação por JavaScript. */
  const entrada = (ordem) => ({ animationDelay: `${0.1 + ordem * 0.09}s` });

  const contagem =
    diasRestantes === null
      ? null
      : diasRestantes === 0
      ? 'Último dia de inscrições'
      : diasRestantes === 1
      ? 'Falta 1 dia'
      : `Faltam ${diasRestantes} dias`;

  return (
    <section
      id="processo-seletivo"
      className="relative flex items-center min-h-[88vh] pt-32 pb-20 md:pt-40 md:pb-24 overflow-hidden bg-night-950 grain"
    >
      {/* Fundo: grade verde + brilhos */}
      <div className="absolute inset-0 bg-grid-green bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_72%)] opacity-70 pointer-events-none" />
      <div className="absolute -top-72 left-1/2 -translate-x-1/2 w-[70rem] h-[70rem] rounded-full bg-primary-700/[0.18] blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-32 w-[38rem] h-[38rem] rounded-full bg-primary-500/[0.09] blur-3xl pointer-events-none" />

      <div className="container-custom relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          {/* ------------------------------------------------ Coluna de texto */}
          <div className="lg:col-span-7">
            <div className="ps-rise flex flex-wrap items-center gap-3" style={entrada(0)}>
              <span className="pill">
                <span className="ati-live" />
                {homeCta.eyebrow}
              </span>
              {contagem && (
                <span className="inline-flex items-center gap-2 rounded-full border border-primary-400/30 bg-primary-400/[0.1] px-3.5 py-1.5 text-xs font-semibold text-primary-300">
                  <CalendarClock size={13} />
                  {contagem}
                </span>
              )}
            </div>

            <h2
              className="ps-rise font-display font-bold text-mist-50 tracking-tightest leading-[1.02] text-[clamp(2.4rem,6.5vw,5.5rem)] mt-6"
              style={entrada(1)}
            >
              {homeCta.title}{' '}
              <span className="text-gradient">{homeCta.titleHighlight}</span>
            </h2>

            <p
              className="ps-rise mt-7 text-base md:text-lg text-mist-300 leading-relaxed max-w-xl"
              style={entrada(2)}
            >
              {homeCta.description}
            </p>

            <div className="ps-rise mt-9 flex flex-col sm:flex-row gap-4" style={entrada(3)}>
              <Link to="/inscricao" className="btn-primary">
                {homeCta.buttonLabel}
                <ArrowRight size={18} />
              </Link>
              <a href="#about" className="btn-secondary">
                {homeCta.secondaryLabel}
              </a>
            </div>

            {psConfig.deadline && (
              <p className="ps-rise mt-6 text-sm text-mist-500" style={entrada(4)}>
                Inscrições até{' '}
                <strong className="text-mist-300 font-semibold">{psConfig.deadline}</strong>
                {' · '}
                <span>leva menos de 15 minutos</span>
              </p>
            )}
          </div>

          {/* ------------------------------------------- Coluna das trilhas */}
          <div className="ps-rise lg:col-span-5" style={entrada(3)}>
            <div className="ati-telemetry p-6 md:p-7">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.22em] text-mist-400 mb-6">
                <span className="ati-live" />
                Duas trilhas, um time
              </div>

              <div className="space-y-3">
                {homeCta.trilhas?.map((trilha, i) => (
                  <div
                    key={trilha.nome}
                    className="flex items-start gap-4 rounded-2xl border border-white/[0.08] bg-white/[0.03] px-5 py-4"
                  >
                    <span className="h-10 w-10 shrink-0 rounded-xl bg-primary-400/15 text-primary-300 flex items-center justify-center">
                      {i === 0 ? <Code2 size={18} /> : <Sprout size={18} />}
                    </span>
                    <div>
                      <p className="font-semibold text-mist-50">{trilha.nome}</p>
                      <p className="text-sm text-mist-400 leading-relaxed mt-0.5">
                        {trilha.descricao}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {cronograma?.length > 0 && (
                <div className="mt-6 pt-5 border-t border-white/[0.07] grid grid-cols-2 gap-4">
                  {cronograma.slice(0, 2).map((item) => (
                    <div key={item.label}>
                      <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-mist-500">
                        {item.label}
                      </p>
                      <p className="font-display text-xl font-bold text-mist-50 mt-1">
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Dica de rolagem — sinaliza que o site continua abaixo */}
        <div
          className="ps-rise mt-14 flex items-center justify-center gap-2 text-mist-500 text-[10px] uppercase tracking-[0.25em]"
          style={entrada(5)}
        >
          <span>conheça a liga</span>
          <ArrowDown size={14} className="animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default ProcessoSeletivoCTA;
